import React, { useEffect, useState, useContext } from 'react';
import { useAuth } from '../AuthContext';
import { listBuddies, fetchGroupSpeciesMultipliers, updateGroupSpeciesMultipliers, fetchLeaderboardInitData } from '../api';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Box, Typography, Avatar, Card, CardContent, Grid, FormControl, InputLabel, Select, MenuItem, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Chip, Switch, useMediaQuery } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { API_URL } from '../config';

const fishIcons = {
  hecht: '/fish-icons/hecht.png',
  barsch: '/fish-icons/barsch.png',
  zander: '/fish-icons/zander.png',
  // Add more species as needed
};

const MOBILE_ROW_DIVIDER = '1px solid rgba(255, 255, 255, 0.12)';

function renderFishCell(fishList) {
  if (!fishList || fishList.length === 0) return '';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
      {fishList.map((fish, idx) => (
        <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mx: 1 }}>
          <img
            src={fishIcons[fish.species.toLowerCase()] || '/fish-icons/default.png'}
            alt={fish.species}
            style={{
              width: 36,
              height: 36,
              objectFit: 'contain',
              // tint the fish icons towards a warm gold tone
              filter: 'grayscale(1) sepia(1) hue-rotate(20deg) saturate(5) brightness(1.1) drop-shadow(0 1px 2px rgba(212, 175, 55, 0.6))',
            }}
          />
          {fish.size && <span style={{ fontSize: 14 }}>{fish.size} cm</span>}
        </Box>
      ))}
    </Box>
  );
}

function renderFishCellStacked(fishList) {
  if (!fishList || fishList.length === 0) return '-';

  // Group all catches by species
  const speciesMap = {};
  for (const fish of fishList) {
    if (!fish || !fish.species) continue;
    const key = fish.species;
    if (!speciesMap[key]) speciesMap[key] = [];
    speciesMap[key].push(fish);
  }

  // For each species, take up to the two largest fish (by size)
  const speciesList = Object.entries(speciesMap).map(([species, fishes]) => {
    const sorted = [...fishes].sort((a, b) => (b.size || 0) - (a.size || 0));
    return { species, fishes: sorted.slice(0, 2) };
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      {speciesList.map(({ species, fishes }, idx) => (
        <Box key={species || idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          {fishes.map((fish, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <img
                src={fishIcons[species.toLowerCase()] || '/fish-icons/default.png'}
                alt={species}
                style={{
                  width: 24,
                  height: 24,
                  objectFit: 'contain',
                  filter: 'grayscale(1) sepia(1) hue-rotate(20deg) saturate(5) brightness(1.1) drop-shadow(0 1px 2px rgba(212, 175, 55, 0.6))',
                }}
              />
              {fish.size && (
                <Typography variant="caption">
                  {fish.size} cm
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

export default function DashboardLeaderboard({ groupId, groups = [], selectedGroup, onGroupChange, refreshKey }) {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleLogout = () => {
    logout();
  };
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [groupDetail, setGroupDetail] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [activeFrom, setActiveFrom] = useState('');
  const [activeTo, setActiveTo] = useState('');
  const [members, setMembers] = useState([]);
  const [buddies, setBuddies] = useState([]);
  const [selectedBuddyIds, setSelectedBuddyIds] = useState([]);
  const [buddyAdminIds, setBuddyAdminIds] = useState([]);
  const [removeMemberIds, setRemoveMemberIds] = useState([]);
  const [speciesItems, setSpeciesItems] = useState([]);
  const [speciesEdits, setSpeciesEdits] = useState({});

  useEffect(() => {
    if (!groupId) return;
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    // Load group details and leaderboard in one batch call (2→1)
    fetchLeaderboardInitData(token, groupId)
      .then(batch => {
        setData(batch.leaderboard || []);
        const group = batch.group || {};
        setGroupName(group?.name || '');
        setGroupDetail(group);
        setEditName(group?.name || '');
        setMembers(group?.members || []);
        setActiveFrom(group?.active_from ? toDatetimeLocal(group.active_from) : '');
        setActiveTo(group?.active_to ? toDatetimeLocal(group.active_to) : '');
        const existingIds = (group.members || []).map(m => m.user_id);
        const existingAdminIds = (group.members || []).filter(m => m.role === 'admin').map(m => m.user_id);
        setSelectedBuddyIds(existingIds);
        setBuddyAdminIds(existingAdminIds);
        setRemoveMemberIds([]);
      })
      .catch(() => {
        // Fallback to individual fetches
        fetch(`${API_URL}/groups/${groupId}/leaderboard`, { headers })
          .then(res => res.ok ? res.json() : [])
          .then(data => setData(data));
        fetch(`${API_URL}/groups/${groupId}`, { headers })
          .then(res => res.ok ? res.json() : null)
          .then(group => {
            if (!group) return;
            setGroupName(group?.name || '');
            setGroupDetail(group);
            setEditName(group?.name || '');
            setMembers(group?.members || []);
            setActiveFrom(group?.active_from ? toDatetimeLocal(group.active_from) : '');
            setActiveTo(group?.active_to ? toDatetimeLocal(group.active_to) : '');
            const existingIds = (group.members || []).map(m => m.user_id);
            const existingAdminIds = (group.members || []).filter(m => m.role === 'admin').map(m => m.user_id);
            setSelectedBuddyIds(existingIds);
            setBuddyAdminIds(existingAdminIds);
            setRemoveMemberIds([]);
          });
      })
      .finally(() => setLoading(false));
    
    // Load species multipliers for this group
    (async () => {
      try {
        if (!token) return;
        const items = await fetchGroupSpeciesMultipliers(token, groupId);
        setSpeciesItems(items || []);
        // Initialize edits map
        const initial = {};
        for (const it of items || []) initial[it.species_id] = it.multiplier;
        setSpeciesEdits(initial);
      } catch {
        setSpeciesItems([]);
        setSpeciesEdits({});
      }
    })();
  }, [groupId, refreshKey, token]);

  // When opening settings, reload fresh group details and species multipliers
  useEffect(() => {
    if (!settingsOpen || !groupId) return;
    (async () => {
      try {
        // Refresh group details
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const group = await fetch(`${API_URL}/groups/${groupId}`, { headers }).then(r => r.ok ? r.json() : null);
        if (group) {
          setGroupDetail(group);
          setEditName(group?.name || '');
          setMembers(group?.members || []);
          setActiveFrom(group?.active_from ? toDatetimeLocal(group.active_from) : '');
          setActiveTo(group?.active_to ? toDatetimeLocal(group.active_to) : '');
          const existingIds = (group.members || []).map(m => m.user_id);
          const existingAdminIds = (group.members || []).filter(m => m.role === 'admin').map(m => m.user_id);
          setSelectedBuddyIds(existingIds);
          setBuddyAdminIds(existingAdminIds);
          setRemoveMemberIds([]);
        }
        // Refresh species multipliers
        if (token) {
          const items = await fetchGroupSpeciesMultipliers(token, groupId);
          setSpeciesItems(items || []);
          const initial = {};
          for (const it of items || []) initial[it.species_id] = it.multiplier;
          setSpeciesEdits(initial);
        }
      } catch {
        // ignore; dialog will show current state
      }
    })();
  }, [settingsOpen, groupId, token]);

  useEffect(() => {
    const loadBuddies = async () => {
      try {
        if (!token) return;
        const data = await listBuddies(token);
        setBuddies(Array.isArray(data) ? data : []);
      } catch {
        setBuddies([]);
      }
    };
    loadBuddies();
  }, [token]);

  function toDatetimeLocal(iso) {
    try {
      const d = new Date(iso);
      const pad = (n) => `${n}`.padStart(2, '0');
      const yyyy = d.getFullYear();
      const mm = pad(d.getMonth() + 1);
      const dd = pad(d.getDate());
      const hh = pad(d.getHours());
      const mi = pad(d.getMinutes());
      return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
    } catch { return ''; }
  }

  const isGroupAdmin = (() => {
    if (!user || !groupDetail || !groupDetail.members) return false;
    const me = groupDetail.members.find(m => m.user_id === user.id);
    return me?.role === 'admin';
  })();

  async function saveGroup() {
    try {
      const payload = { name: editName.trim() };
      if (activeFrom) payload.active_from = new Date(activeFrom).toISOString();
      if (activeTo) payload.active_to = new Date(activeTo).toISOString();
      const res = await fetch(`${API_URL}/groups/${groupId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to update group');
      const updated = await res.json();
      setGroupDetail(updated);
      setGroupName(updated.name || '');
      setMembers(updated.members || []);
      setActiveFrom(updated.active_from ? toDatetimeLocal(updated.active_from) : '');
      setActiveTo(updated.active_to ? toDatetimeLocal(updated.active_to) : '');
      // Add newly selected buddies and promote admins similar to Create Group dialog
      const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      for (const uid of selectedBuddyIds) {
        await fetch(`${API_URL}/groups/${groupId}/join?user_id=${uid}`, { method: 'POST', headers });
        if (buddyAdminIds.includes(uid)) {
          await fetch(`${API_URL}/groups/${groupId}/assign-role?user_id=${uid}&role=admin`, { method: 'POST', headers });
        }
      }
      // Remove deselected existing members
      for (const uid of removeMemberIds) {
        await fetch(`${API_URL}/groups/${groupId}/leave?user_id=${uid}`, { method: 'POST', headers });
      }
      // Refresh after membership changes
      const refreshed = await fetch(`${API_URL}/groups/${groupId}`).then(r => r.ok ? r.json() : null);
      if (refreshed) {
        setMembers(refreshed.members || []);
        setGroupDetail(refreshed);
      }
      // Persist only changed species multipliers in a single batch
      const changedItems = speciesItems
        .map(it => {
          const edited = speciesEdits[it.species_id];
          if (edited === undefined || edited === null || edited === '') return null;
          const newVal = Number(edited);
          if (!Number.isFinite(newVal)) return null;
          if (Number(newVal) === Number(it.multiplier)) return null;
          return { species_id: it.species_id, multiplier: newVal };
        })
        .filter(Boolean);
      if (changedItems.length > 0) {
        await updateGroupSpeciesMultipliers(token, groupId, changedItems);
      }
      // Refresh leaderboard to reflect new multipliers
      fetch(`${API_URL}/groups/${groupId}/leaderboard`)
        .then(res => res.ok ? res.json() : [])
        .then(data => setData(data));
      // Reset selection
      setSelectedBuddyIds([]);
      setBuddyAdminIds([]);
      setRemoveMemberIds([]);
      setSpeciesEdits({});
      setSettingsOpen(false);
    } catch (e) {
      // Optional: surface error
    }
  }

  // Role change happens via invite list; existing members are shown selected and can toggle admin there.

  if (!groupId) return null;

  return (
    <Grid container justifyContent="center" sx={{ mt: 4, mb: 4 }}>
      <Grid item xs={12} md={10} lg={8}>
        <Card
          elevation={3}
          sx={{
            borderRadius: 4,
            p: 2,
            border: '1px solid rgba(212, 175, 55, 0.4)',
            boxShadow: '0 0 20px rgba(212, 175, 55, 0.1)',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 1 }}>
                  Leaderboard: {groupName}
                </Typography>
                {isGroupAdmin && (
                  <IconButton aria-label="settings" size="small" onClick={() => setSettingsOpen(true)}>
                    <SettingsIcon />
                  </IconButton>
                )}
              </Box>
              {groups.length > 0 && (
                <FormControl
                  size="small"
                  variant="outlined"
                  sx={{
                    minWidth: 200,
                    ml: 2,
                    '& .MuiInputLabel-root': {
                      color: 'grey.400',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: 'primary.light',
                    },
                  }}
                >
                  <InputLabel id="group-select-label">Group</InputLabel>
                  <Select
                    labelId="group-select-label"
                    value={selectedGroup}
                    label="Group"
                    onChange={e => onGroupChange && onGroupChange(e.target.value)}
                    sx={{
                      bgcolor: 'background.paper',
                      color: 'text.primary',
                      borderRadius: 2,
                      '& .MuiSelect-icon': {
                        color: 'grey.400',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'grey.700',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.light',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: 'background.paper',
                          color: 'text.primary',
                        },
                      },
                    }}
                  >
                    {groups.map(g => (
                      <MenuItem key={g.group_id} value={g.group_id}>
                        {g.group_name} ({g.role})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            {isMobile ? (
              <Box sx={{ overflowX: 'auto', mt: 1 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `100px repeat(${data.length || 1}, minmax(110px, 1fr))`,
                    gap: 0.5,
                    alignItems: 'stretch',
                  }}
                >
                  {/* Header row: user columns */}
                  <Box sx={{}} />
                  {data.map((row, idx) => (
                    <Box
                      key={row.user_id || idx}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                        p: 1,
                        borderRadius: 2,
                        bgcolor: 'rgba(0,0,0,0.35)',
                        cursor: row.user_id ? 'pointer' : 'default',
                      }}
                      onClick={() => {
                        if (row.user_id) navigate(`/profile/${row.user_id}`);
                      }}
                    >
                      {row.avatar_url && (
                        <Avatar
                          src={(() => {
                            const pic = row.avatar_url;
                            if (!pic) return undefined;
                            if (pic.startsWith('http://') || pic.startsWith('https://')) return pic;
                            const normalized = pic.startsWith('/') ? pic.slice(1) : pic;
                            return `${API_URL}/${normalized}`;
                          })()}
                          alt={row.nickname}
                          sx={{ width: 36, height: 36, mb: 0.5 }}
                        />
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {row.nickname}
                      </Typography>
                      {row.team && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                          {row.team}
                        </Typography>
                      )}
                    </Box>
                  ))}

                  {/* POS row */}
                  <Box
                    sx={{
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      py: 0.5,
                    }}
                  >
                    POS
                  </Box>
                  {data.map((row, idx) => (
                    <Box
                      key={(row.user_id || idx) + '-pos'}
                      sx={{ textAlign: 'center', fontSize: 13, py: 0.5 }}
                    >
                      {idx + 1}
                    </Box>
                  ))}

                  {/* TEAM row */}
                  <Box
                    sx={{
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      py: 0.5,
                      borderTop: MOBILE_ROW_DIVIDER,
                    }}
                  >
                    TEAM
                  </Box>
                  {data.map((row, idx) => (
                    <Box
                      key={(row.user_id || idx) + '-team'}
                      sx={{ textAlign: 'center', fontSize: 12, py: 0.5, borderTop: MOBILE_ROW_DIVIDER }}
                    >
                      <Typography variant="body2" noWrap>
                        {row.team || row.nickname}
                      </Typography>
                    </Box>
                  ))}

                  {/* FIXED row */}
                  <Box
                    sx={{
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      py: 0.5,
                      borderTop: MOBILE_ROW_DIVIDER,
                    }}
                  >
                    FIXED
                  </Box>
                  {data.map((row, idx) => (
                    <Box
                      key={(row.user_id || idx) + '-fixed'}
                      sx={{ textAlign: 'center', fontSize: 12, py: 0.5, borderTop: MOBILE_ROW_DIVIDER }}
                    >
                        {row.fixed && row.fixed.length > 0 ? renderFishCellStacked(row.fixed) : '-'}
                    </Box>
                  ))}

                  {/* VARIOUS row */}
                  <Box
                    sx={{
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      py: 0.5,
                      borderTop: MOBILE_ROW_DIVIDER,
                    }}
                  >
                    VARIOUS
                  </Box>
                  {data.map((row, idx) => (
                    <Box
                      key={(row.user_id || idx) + '-various'}
                      sx={{ textAlign: 'center', fontSize: 12, py: 0.5, borderTop: MOBILE_ROW_DIVIDER }}
                    >
                        {row.various && row.various.length > 0 ? renderFishCellStacked(row.various) : '-'}
                    </Box>
                  ))}

                  {/* BONUS POINTS row */}
                  <Box
                    sx={{
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      py: 0.5,
                      borderTop: MOBILE_ROW_DIVIDER,
                    }}
                  >
                    BONUS POINTS
                  </Box>
                  {data.map((row, idx) => (
                    <Box
                      key={(row.user_id || idx) + '-bonus'}
                      sx={{ textAlign: 'center', fontSize: 13, py: 0.5, borderTop: MOBILE_ROW_DIVIDER }}
                    >
                      {row.bonus_points ?? ''}
                    </Box>
                  ))}

                  {/* TOTAL POINTS row */}
                  <Box
                    sx={{
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      color: '#D4AF37',
                      py: 0.5,
                      borderTop: MOBILE_ROW_DIVIDER,
                    }}
                  >
                    TOTAL POINTS
                  </Box>
                  {data.map((row, idx) => (
                    <Box
                      key={(row.user_id || idx) + '-total'}
                      sx={{ textAlign: 'center', fontSize: 14, py: 0.5, fontWeight: 700, color: 'primary.main', borderTop: MOBILE_ROW_DIVIDER }}
                    >
                      {row.total_points ?? row.score ?? ''}
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 3,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                }}
              >
                <Table sx={{ minWidth: 650, borderCollapse: 'separate', borderSpacing: 0 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, backgroundColor: 'rgba(212, 175, 55, 0.06)' }}>POS</TableCell>
                      <TableCell sx={{ fontWeight: 700, backgroundColor: 'rgba(212, 175, 55, 0.06)' }}>TEAM</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: 'rgba(212, 175, 55, 0.06)' }}>FIXED</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: 'rgba(212, 175, 55, 0.06)' }}>VARIOUS</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: 'rgba(212, 175, 55, 0.06)' }}>BONUS POINTS</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: 'rgba(212, 175, 55, 0.06)', color: '#D4AF37' }}>TOTAL POINTS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.map((row, idx) => (
                      <TableRow key={row.user_id || idx} hover sx={{ transition: 'background 0.2s' }}>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: 18 }}>{idx + 1}</TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, cursor: row.user_id ? 'pointer' : 'default' }}
                            onClick={() => {
                              if (row.user_id) navigate(`/profile/${row.user_id}`);
                            }}
                          >
                            {row.avatar_url && (
                              <Avatar
                                src={(() => {
                                  const pic = row.avatar_url;
                                  if (!pic) return undefined;
                                  if (pic.startsWith('http://') || pic.startsWith('https://')) return pic;
                                  const normalized = pic.startsWith('/') ? pic.slice(1) : pic;
                                  return `${API_URL}/${normalized}`;
                                })()}
                                alt={row.nickname}
                                sx={{ width: 40, height: 40, mb: 0.5 }}
                              />
                            )}
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{row.nickname}</Typography>
                            {row.team && <Typography variant="caption">{row.team}</Typography>}
                          </Box>
                        </TableCell>
                        <TableCell align="center">{renderFishCell(row.fixed)}</TableCell>
                        <TableCell align="center">{renderFishCell(row.various)}</TableCell>
                        <TableCell align="center">{row.bonus_points ?? ''}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: 18 }}>{row.total_points ?? row.score ?? ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} fullWidth maxWidth="sm">
              <DialogTitle>Group Settings</DialogTitle>
              <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField label="Group name" value={editName} onChange={(e) => setEditName(e.target.value)} fullWidth />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField label="Start" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} value={activeFrom} onChange={(e) => setActiveFrom(e.target.value)} />
                    <TextField label="End" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} value={activeTo} onChange={(e) => setActiveTo(e.target.value)} />
                  </Box>
                  {isGroupAdmin && (
                    <>
                      <Typography variant="subtitle2">Invite buddies (toggle admin)</Typography>
                      <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
                        {buddies.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">No buddies yet.</Typography>
                        ) : (
                          buddies.map((u) => (
                            <Box key={u.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar
                                  src={(() => {
                                    const pic = u.profile_picture;
                                    if (!pic) return undefined;
                                    if (pic.startsWith('http://') || pic.startsWith('https://')) return pic;
                                    const normalized = pic.startsWith('/') ? pic.slice(1) : pic;
                                    return `${API_URL}/${normalized}`;
                                  })()}
                                  alt={u.nickname}
                                  sx={{ width: 28, height: 28 }}
                                />
                                <Typography variant="body2">{u.nickname || u.email}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Button
                                  size="small"
                                  variant={selectedBuddyIds.includes(u.id) ? 'contained' : 'outlined'}
                                  onClick={() => {
                                    const isMember = members.some(m => m.user_id === u.id);
                                    setSelectedBuddyIds((prev) => {
                                      if (prev.includes(u.id)) {
                                        // Deselect
                                        const next = prev.filter((id) => id !== u.id);
                                        if (isMember) {
                                          setRemoveMemberIds((rm) => rm.includes(u.id) ? rm : [...rm, u.id]);
                                        }
                                        // Also remove admin toggle if deselected
                                        setBuddyAdminIds((adm) => adm.filter((id) => id !== u.id));
                                        return next;
                                      } else {
                                        // Select
                                        const next = [...prev, u.id];
                                        if (isMember) {
                                          // If previously marked for removal, unmark
                                          setRemoveMemberIds((rm) => rm.filter((id) => id !== u.id));
                                        }
                                        return next;
                                      }
                                    });
                                  }}
                                >
                                  {selectedBuddyIds.includes(u.id) ? (members.some(m => m.user_id === u.id) ? 'Member' : 'Selected') : (members.some(m => m.user_id === u.id) ? 'Remove' : 'Select')}
                                </Button>
                                <Button
                                  size="small"
                                  variant={buddyAdminIds.includes(u.id) ? 'contained' : 'outlined'}
                                  onClick={() => {
                                    setBuddyAdminIds((prev) => (
                                      prev.includes(u.id) ? prev.filter((id) => id !== u.id) : [...prev, u.id]
                                    ));
                                  }}
                                  disabled={!selectedBuddyIds.includes(u.id)}
                                >
                                  Admin
                                </Button>
                              </Box>
                            </Box>
                          ))
                        )}
                      </Box>
                      <Typography variant="subtitle2" sx={{ mt: 2 }}>Species multipliers</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {speciesItems.map((it) => (
                          <Box key={it.species_id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2">{it.name}</Typography>
                            <TextField
                              size="small"
                              type="number"
                              inputProps={{ step: '0.1' }}
                              value={speciesEdits[it.species_id] ?? it.multiplier}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setSpeciesEdits((prev) => ({ ...prev, [it.species_id]: isNaN(val) ? 0 : val }));
                              }}
                              sx={{ width: 120 }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                  {/** Removed separate member role edit section; handled via invite list above. **/}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={saveGroup}>Save</Button>
              </DialogActions>
            </Dialog>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
